/**
 * SortableTable — generic sortable table for Engagement / Quality / Operations
 * tabs (IA §3.1.5 Top Users, §4.1.5 Quality by user, §7.1.4 Error breakdown,
 * §7.1.5 Tool usage, §7.1.6 Skills usage). Milestone v1.4 / Plan 03-05 Task 5.2.
 *
 * Rendering contract:
 *   - Tailwind table on `bg-card / border-border` tokens — mirrors the look
 *     of the other widgets on the tab (KPICard, TrendChart).
 *   - Columns with `sortable: true` render their header as a button; click
 *     cycles asc → desc → cleared. "Cleared" returns to `defaultSort` (or no
 *     sort if no default provided).
 *   - Row click → `onRowClick(row)` when provided; adds `cursor-pointer`
 *     and a soft hover row highlight.
 *   - Loading state: a skeleton as tall as this table's last render, so a
 *     40-row table does not collapse to a handful of rows and back on every
 *     filter change. `SKELETON_ROWS` is the cold-tab fallback only.
 *   - Empty state: single-cell message spanning all columns (default "No rows").
 *   - Optional "Load more" button shown when `onLoadMore` is provided and the
 *     visible row count is at or above `initialLimit` (so the first call is
 *     the user's explicit choice).
 *
 * Sort state lives in the URL via the Task 5.1 helpers (`readTableSort` /
 * `writeTableSort`) keyed by the `tableId` prop. Two SortableTables on the
 * same page pick different ids, keeping their URL params isolated
 * (`?topUsers_sort=...&qualityByUser_sort=...`).
 */

import * as React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  readTableSort,
  writeTableSort,
  type TableSort,
} from '@/lib/filters-schema';
import { useUrlFilters } from '@/hooks/use-url-filters';
import { cn } from '@/lib/utils';

export interface SortableColumn<T> {
  /** Field key on the row object — also used as the sort identifier. */
  key: keyof T & string;
  header: string;
  sortable?: boolean;
  /** Render a cell. Defaults to `String(value)`. */
  format?: (value: unknown, row: T) => React.ReactNode;
  /** Optional fixed width (Tailwind class, e.g. `w-32`). */
  width?: string;
  align?: 'left' | 'right';
}

export interface SortableTableProps<T> {
  /** Stable URL-state key — lets two sortable tables coexist without collisions. */
  tableId: string;
  columns: Array<SortableColumn<T>>;
  rows: T[];
  /**
   * Returns a stable React key for the given row — required, no fallback.
   * Every current call site has a natural key (userId / slackId / errorClass /
   * tool / skill / bucket). Avoiding the old `row.id ?? i` path removes the
   * DOM-reuse-across-sort-transitions class of bug: an index key makes React
   * associate different rows with the same DOM node when the sort changes,
   * leaking per-row state (tooltip hover, inline edit focus) to the wrong row.
   */
  getRowKey: (row: T) => string;
  /**
   * Mobile-viewport renderer (Agent N — mobile UX pass; Operations P2-1).
   *
   * When provided, the table renders as a stacked card list at `<md` and
   * the HTML `<table>` takes over at `≥md`. Mobile cards sidestep the
   * horizontal-scroll + clipped-last-column problem on 375px viewports
   * without sacrificing the full column set on desktop.
   *
   * Each row becomes one card (bg-card, rounded, p-3) rendered by the
   * caller — caller owns the label/value layout so per-page semantics
   * (e.g. role badge + relative time) stay intact. Sort + row-click
   * affordances are preserved: a "Sort by" `<Select>` sits above the card
   * list (reusing the sortable column defs), and tapping the card runs
   * `onRowClick`. When `onRowClick` is provided, cards get `role="button"`
   * + `tabIndex={0}` + Enter/Space handlers so keyboard users can activate
   * them, plus `cursor-pointer` + `hover`/`focus` states (addresses
   * Operations P2-5 "rows clickable but no affordance").
   *
   * When omitted, the table renders on all viewports (legacy behavior).
   * Narrow / naturally-fit tables (ErrorBreakdown, Tool/Skill usage) can
   * keep their existing overflow-x-auto + sticky-first-column scrolling.
   *
   * ### TODO(audit): consumer migration
   * - `mobileCardRender` is the legacy prop name; `mobileRender` (below) is
   *   the new canonical spelling. Both are honored — when both are passed,
   *   `mobileRender` wins. Page agents on OperationsPage (Tool usage / Skill
   *   usage / Error breakdown) and the Feedback page should adopt
   *   `mobileRender` in the next round so tables stop clipping on 390px
   *   viewports (Operations P2-1).
   */
  mobileCardRender?: (row: T) => React.ReactNode;
  /**
   * New canonical mobile renderer (Operations P2-1). Behaves identically to
   * `mobileCardRender`; kept as an alias so consumers can migrate without
   * a breaking rename. When both are provided, `mobileRender` wins.
   *
   * ### TODO(audit): OperationsPage (Tool usage / Skill usage / Error
   * breakdown) and Feedback page agents should supply `mobileRender` so
   * the tables stop clipping on mobile viewports. Existing consumers that
   * still pass `mobileCardRender` keep working unchanged.
   */
  mobileRender?: (row: T) => React.ReactNode;
  /** Fallback sort when the URL has no entry for `tableId`. */
  defaultSort?: TableSort;
  /** Row click handler — `cursor-pointer` applied only when provided. */
  onRowClick?: (row: T) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Rows shown initially; "Load more" appears when rows.length >= initialLimit. */
  initialLimit?: number;
  /** Called when the user clicks "Load more"; caller bumps its request limit. */
  onLoadMore?: () => void;
  /**
   * Freeze the first column when the table overflows horizontally (IA §9,
   * Plan 04-10 mobile polish). Default `true` — legacy call sites inherit
   * the sticky behavior, which is invisible on desktop (wrapper stays
   * narrower than viewport) but on mobile keeps the identifier column
   * (usually `username`) pinned while the rest scrolls.
   *
   * Opt out (`false`) when the first column is not a stable identifier — e.g.
   * the error-breakdown table, where the first column is already the class
   * name and the table is narrow enough to avoid horizontal scroll even at
   * 375px.
   */
  stickyFirstColumn?: boolean;
  /**
   * bug F4 — pin the header row to the top of the scroll container while
   * the body scrolls vertically. When `true`, the scrollable wrapper
   * becomes `overflow-y-auto` (with the supplied `maxHeight`), and
   * `<thead>` cells become `sticky top-0`. Off by default; turn on for
   * long top-user-style tables where the user otherwise loses column
   * context mid-scroll.
   */
  stickyHeader?: boolean;
  /**
   * Tailwind max-height utility applied to the scroll wrapper when
   * `stickyHeader` is true (e.g. `'max-h-[520px]'`). Ignored when
   * `stickyHeader` is false.
   */
  maxHeight?: string;
  /**
   * Inline row-expand support (Phase 8 — BriefsPage).
   *
   * When BOTH `renderExpanded` and `expandedKeys` are provided, each row
   * whose `getRowKey(row)` is in `expandedKeys` renders the result of
   * `renderExpanded(row)` as a full-width sub-row directly below it
   * (desktop) or as inline content below the card (mobile). A trailing
   * "expand" column with a chevron toggle is appended automatically;
   * clicking the chevron fires `onToggleExpanded(row)` without bubbling to
   * `onRowClick` (so navigation-on-click semantics are preserved).
   *
   * Mobile-gesture rule (Phase 8 fix): on the stacked card list a body tap
   * toggles expansion when the table is expandable — the inline panel is
   * the primary mobile affordance, so eating the tap there matches user
   * expectation. When `onRowClick` is also wired, a sibling "→" icon
   * button surfaces the navigate path so it stays one tap away. Desktop
   * keeps the legacy "row tap → onRowClick + chevron column toggles
   * expand" model.
   *
   * `onToggleExpanded` is also wired to keyboard a11y: pressing Enter or
   * Space on a focused expand button toggles, and pressing Escape on a
   * focused row closes its expansion if open.
   *
   * Omitted by default — tables that don't expand rows pay no DOM cost.
   */
  renderExpanded?: (row: T) => React.ReactNode;
  expandedKeys?: ReadonlySet<string>;
  onToggleExpanded?: (row: T) => void;
  /**
   * Extra classes forwarded to the underlying `<Table>` (ui/table.tsx) element.
   *
   * The shared table primitive defaults to `min-w-full w-max` + auto layout,
   * which lets an intrinsically wide cell (e.g. a full-width expanded colSpan
   * sub-row) grow the table and re-distribute column widths — a visible column
   * shift on expand. Pass `table-fixed w-full` here to lock column widths to
   * the header cells so the expanded row can never reflow them. Scoped per
   * call site so other tables keep the primitive's default layout.
   */
  tableClassName?: string;
}

/** Next sort — 2-state toggle (asc ⇄ desc). First click on a column uses
 *  `firstDir` so numeric ("align=right") columns start at desc (most common
 *  use: "top N"), text columns start at asc (alphabetical). No third
 *  "cleared" state — that was confusing in v1.3 (3rd click reverted silently
 *  to the default sort which wasn't obvious from the UI). */
function cycleSort(
  current: TableSort | undefined,
  column: string,
  firstDir: 'asc' | 'desc',
): TableSort {
  if (!current || current.column !== column) return { column, dir: firstDir };
  return { column, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

function SortArrow({ dir }: { dir: 'asc' | 'desc' | null }): React.ReactElement {
  // Muted fixed-width so the header doesn't shift when the arrow toggles.
  return (
    <span
      aria-hidden="true"
      className={cn(
        'ml-1 inline-block w-3 text-center text-[10px] font-normal text-muted-foreground',
      )}
    >
      {dir === 'asc' ? '▲' : dir === 'desc' ? '▼' : ''}
    </span>
  );
}

/**
 * Skeleton body used while `isLoading`, `rows` tall — the height of THIS
 * table's last render, so a 40-row table does not collapse to a handful of
 * rows and back on every filter change. `SKELETON_ROWS` is only the cold-tab
 * fallback, for the one load where there is no previous count to remember.
 */
function LoadingSkeleton<T>({
  columns,
  rows,
}: {
  columns: Array<SortableColumn<T>>;
  rows: number;
}): React.ReactElement {
  return (
    <TableBody data-testid="sortable-table-skeleton">
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {columns.map((c) => (
            <TableCell key={c.key}>
              <div className="animate-pulse h-6 bg-muted rounded" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  );
}

/**
 * Tailwind class bundle for sticky-first-column cells.
 *
 * - `sticky left-0`: pins the cell to the scroll container's left edge.
 * - `bg-card`: solid background so scrolled rows don't bleed through.
 * - `z-10`: keeps the pinned column above scrolled body cells (headers already
 *   sit in the normal flow — Radix shadcn table has no sticky header today).
 * - `shadow-[...]`: subtle right edge indicating scrollable content.
 *
 * Applied to the first <th> AND first <td>; both must match so the column
 * scrolls as a visual unit.
 */
/** Cold-tab fallback only: the number of skeleton rows to show when this
 *  table has never rendered any. */
const SKELETON_ROWS = 5;

const STICKY_FIRST_COL_CLASS =
  'sticky left-0 bg-card z-10 shadow-[2px_0_4px_rgba(0,0,0,0.04)]';

export function SortableTable<T>({
  tableId,
  columns,
  rows,
  getRowKey,
  defaultSort,
  onRowClick,
  isLoading = false,
  emptyMessage = 'No rows',
  initialLimit = 25,
  onLoadMore,
  stickyFirstColumn = true,
  stickyHeader = false,
  maxHeight,
  mobileCardRender,
  mobileRender,
  renderExpanded,
  expandedKeys,
  onToggleExpanded,
  tableClassName,
}: SortableTableProps<T>): React.ReactElement {
  /* How many rows this table last showed. A skeleton that is not the size of
     what it replaces is a layout jump, and since the recount rule it is paid
     on every filter change rather than once per session — worst exactly where
     the tables are biggest. Recorded after the render that showed them, so a
     render that has already blanked cannot overwrite it; the fallback is for
     a cold tab, where there is no previous count to remember. */
  const lastRowCount = React.useRef(SKELETON_ROWS);
  React.useEffect(() => {
    if (rows.length > 0) lastRowCount.current = rows.length;
  }, [rows.length]);
  const skeletonRows = lastRowCount.current;

  // Expand support is opt-in: BOTH a renderer and a key-set must be supplied
  // so callers can't half-wire the feature (e.g. provide a renderer with no
  // way to track expanded keys).
  const expandable = !!renderExpanded && !!expandedKeys;
  // Total column count for the full-width sub-row colSpan, accounting for
  // the trailing expand column when expandable.
  const totalCols = columns.length + (expandable ? 1 : 0);
  const { filters, commit } = useUrlFilters();
  const stored = readTableSort(filters, tableId);
  const effective: TableSort | undefined = stored ?? defaultSort;
  // Prefer the new prop spelling when both are provided; fall back to the
  // legacy name so existing consumers (EngagementPage / QualityPage /
  // BriefStatusTable) keep working unchanged while page agents migrate.
  const mobileRenderFn = mobileRender ?? mobileCardRender;

  const handleHeaderClick = React.useCallback(
    (columnKey: string, firstDir: 'asc' | 'desc') => {
      // Toggle asc ⇄ desc based on the currently effective sort (stored
      // overrides defaultSort when present). First click on a fresh column
      // uses firstDir — desc for numeric columns, asc for text.
      const next = cycleSort(stored ?? defaultSort, columnKey, firstDir);
      const patched = writeTableSort(filters, tableId, next);
      // Sort is a one-click gesture, not part of the Apply-buffer workflow —
      // commit directly to the URL so the API refetches on the same tick
      // without requiring the user to hit Apply afterward.
      commit({ sort: patched.sort });
    },
    [commit, defaultSort, filters, stored, tableId],
  );

  const hasRows = rows.length > 0;
  const showLoadMore = !isLoading && !!onLoadMore && rows.length >= initialLimit;

  // Mobile card list — rendered only when caller provides `mobileRender` /
  // `mobileCardRender`. Kept sibling-of (not swapped via useViewport) so the
  // desktop table still hydrates as SSR-friendly and a resize across the md
  // breakpoint is a pure CSS swap, not a remount that wipes scroll/sort state.
  const sortableCols = columns.filter((c) => c.sortable);

  // Mobile sort select change handler. Encodes `column.dir` in the value so a
  // single `<Select>` can cover both column-switch and direction-toggle
  // affordances from one tap. Uses the existing `cycleSort` + `writeTableSort`
  // plumbing so URL state stays identical to the desktop header-click path.
  const handleMobileSortChange = React.useCallback(
    (nextValue: string) => {
      const [col, dir] = nextValue.split('.') as [string, 'asc' | 'desc'];
      const patched = writeTableSort(filters, tableId, { column: col, dir });
      commit({ sort: patched.sort });
    },
    [commit, filters, tableId],
  );

  // Current Select value — `column.dir` from the effective sort, or empty
  // when no column is sorted. Radix Select requires a string; undefined =
  // uncontrolled, which we avoid so the trigger always reflects state.
  const mobileSortValue =
    effective && sortableCols.some((c) => c.key === effective.column)
      ? `${effective.column}.${effective.dir}`
      : '';

  const mobileCards = mobileRenderFn ? (
    <div
      data-testid="sortable-table-mobile"
      data-table-id={tableId}
      className="md:hidden space-y-2"
    >
      {/*
        Mobile sort dropdown — single `<Select>` listing each sortable column
        twice (asc + desc). One tap instead of the old pill-per-column grid
        keeps the affordance readable on 360px viewports and reuses the
        project-wide shadcn Select (matches DateRangeControl / FilterModal).
      */}
      {sortableCols.length > 0 && (
        <div
          className="flex items-center gap-2 text-xs"
          data-testid="sortable-mobile-sort"
        >
          <span className="text-muted-foreground shrink-0">Sort by:</span>
          <Select
            value={mobileSortValue || undefined}
            onValueChange={handleMobileSortChange}
          >
            <SelectTrigger
              data-testid="sortable-mobile-sort-trigger"
              // Briefs / Quality audit — sort trigger was 36px, below the
              // 44×44 tap target. Matches SortableTable mobile-card + load-more
              // at `min-h-11`.
              className="h-11 w-auto min-w-[10rem]"
            >
              <SelectValue placeholder="Sort by…" />
            </SelectTrigger>
            <SelectContent className="bg-theme-surface">
              {sortableCols.flatMap((c) => [
                <SelectItem
                  key={`${c.key}.asc`}
                  value={`${c.key}.asc`}
                  data-testid={`sortable-mobile-sort-${c.key}-asc`}
                >
                  {c.header} ▲
                </SelectItem>,
                <SelectItem
                  key={`${c.key}.desc`}
                  value={`${c.key}.desc`}
                  data-testid={`sortable-mobile-sort-${c.key}-desc`}
                >
                  {c.header} ▼
                </SelectItem>,
              ])}
            </SelectContent>
          </Select>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <div
              key={i}
              className="h-20 rounded-lg border border-border bg-card animate-pulse"
            />
          ))}
        </div>
      ) : !hasRows ? (
        <div className="rounded-lg border border-border bg-card p-4 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      ) : (
        <ul className="space-y-2 list-none">
          {rows.map((row) => {
            const key = getRowKey(row);
            const isExpanded = expandable && expandedKeys!.has(key);
            // Phase 8 mobile-gesture fix:
            //   When the table is expandable, tapping the card body toggles
            //   the expansion (the new primary action — read the trace /
            //   brief inline). The legacy "body tap → onRowClick" path is
            //   preserved for tables that aren't expandable so existing
            //   consumers (Engagement / Quality top-N) keep working
            //   unchanged. When BOTH expansion and `onRowClick` are wired
            //   the navigate-affordance moves to a dedicated "→" button
            //   alongside the chevron so primary (expand) and secondary
            //   (open detail page) gestures don't fight each other.
            const cardOnClick = expandable
              ? () => onToggleExpanded?.(row)
              : onRowClick
                ? () => onRowClick(row)
                : undefined;
            const cardInteractive = expandable || !!onRowClick;
            return (
              <li key={key}>
                <div
                  data-testid="sortable-mobile-card"
                  data-row-key={key}
                  data-expanded={isExpanded ? 'true' : 'false'}
                  onClick={cardOnClick}
                  onKeyDown={(e) => {
                    if (expandable && onToggleExpanded) {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleExpanded(row);
                        return;
                      }
                      if (e.key === 'Escape' && isExpanded) {
                        e.preventDefault();
                        onToggleExpanded(row);
                        return;
                      }
                    }
                    if (
                      !expandable &&
                      onRowClick &&
                      (e.key === 'Enter' || e.key === ' ')
                    ) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                  role={cardInteractive ? 'button' : undefined}
                  aria-expanded={expandable ? isExpanded : undefined}
                  tabIndex={cardInteractive ? 0 : undefined}
                  className={cn(
                    'rounded-lg border border-border bg-card p-3 min-h-11',
                    cardInteractive &&
                      'cursor-pointer hover:bg-muted/50 active:bg-muted focus:outline-none focus:ring-2 focus:ring-primary',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <div className="min-w-0 flex-1">{mobileRenderFn(row)}</div>
                    {expandable && onRowClick && (
                      <button
                        type="button"
                        data-testid="sortable-mobile-navigate"
                        aria-label="Open detail page"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRowClick(row);
                        }}
                        className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded hover:bg-muted text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <span aria-hidden="true">→</span>
                      </button>
                    )}
                    {expandable && (
                      <button
                        type="button"
                        data-testid="sortable-mobile-expand"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onToggleExpanded) onToggleExpanded(row);
                        }}
                        className="shrink-0 inline-flex items-center justify-center h-11 w-11 rounded hover:bg-muted text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                      >
                        <span aria-hidden="true">
                          {isExpanded ? '▾' : '▸'}
                        </span>
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div
                    data-testid="sortable-mobile-expanded"
                    data-row-key={key}
                    className="mt-2 rounded-lg border border-border bg-muted/20 p-3"
                  >
                    {renderExpanded!(row)}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {showLoadMore && (
        <div className="flex justify-center pt-1">
          <button
            type="button"
            onClick={onLoadMore}
            className="text-sm font-medium text-primary hover:underline min-h-11 px-4"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  ) : null;

  const desktopTable = (
    <div
      data-testid="sortable-table"
      data-table-id={tableId}
      data-sticky-first-col={stickyFirstColumn ? 'true' : 'false'}
      data-sticky-header={stickyHeader ? 'true' : 'false'}
      // `overflow-x-auto`: on narrow viewports (mobile) the table may exceed the
      // container width; scrolling stays inside the card instead of blowing out
      // the page. `overscroll-x-contain` prevents the scroll from chaining up to
      // the page when the user swipes at the edge.
      // bug F4 — when `stickyHeader` is set, the wrapper also caps its
      // vertical height with `maxHeight` and becomes the scroll container
      // that the `sticky top-0` <thead> cells pin against.
      // Agent N — when the caller also provides `mobileCardRender`, the
      // table is hidden at `<md` in favour of the card list above.
      className={cn(
        'overflow-x-auto overscroll-x-contain rounded-lg border border-border bg-card',
        stickyHeader && 'overflow-y-auto',
        stickyHeader && maxHeight,
        mobileRenderFn && 'hidden md:block',
      )}
    >
      <Table className={tableClassName}>
        <TableHeader>
          <TableRow>
            {columns.map((c, colIdx) => {
              const isSorted = effective && effective.column === c.key;
              const dir = isSorted ? effective.dir : null;
              const isFirstSticky = stickyFirstColumn && colIdx === 0;
              return (
                <TableHead
                  key={c.key}
                  className={cn(
                    'px-3 min-h-11',
                    c.align === 'right' && 'text-right',
                    c.width,
                    isFirstSticky && STICKY_FIRST_COL_CLASS,
                    // bug F4 — sticky header row. `z-20` keeps it above the
                    // sticky-first-column cells (z-10). `bg-background` gives
                    // the pinned row a solid fill so scrolled body rows don't
                    // bleed through, and `border-b` shows a clear separator
                    // against the first visible body row.
                    stickyHeader && 'sticky top-0 z-20 bg-background border-b',
                  )}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      onClick={() =>
                        handleHeaderClick(
                          c.key,
                          c.align === 'right' ? 'desc' : 'asc',
                        )
                      }
                      data-testid={`sortable-header-${c.key}`}
                      data-sort-dir={dir ?? 'none'}
                      className={cn(
                        'inline-flex items-center gap-1 text-left font-medium text-muted-foreground hover:text-foreground transition-colors',
                        c.align === 'right' && 'justify-end text-right',
                      )}
                    >
                      {c.header}
                      <SortArrow dir={dir} />
                    </button>
                  ) : (
                    <span className="font-medium text-muted-foreground">
                      {c.header}
                    </span>
                  )}
                </TableHead>
              );
            })}
            {expandable && (
              <TableHead
                aria-label="Expand"
                className={cn(
                  'px-2 w-10 text-right',
                  stickyHeader && 'sticky top-0 z-20 bg-background border-b',
                )}
              />
            )}
          </TableRow>
        </TableHeader>

        {isLoading ? (
          <LoadingSkeleton columns={columns} rows={skeletonRows} />
        ) : !hasRows ? (
          <TableBody data-testid="sortable-table-empty">
            <TableRow>
              <TableCell
                colSpan={totalCols}
                className="py-6 text-center text-sm text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {rows.map((row) => {
              const key = getRowKey(row);
              const isExpanded = expandable && expandedKeys!.has(key);
              // Body-click default mirrors the mobile card (line ~415):
              //   • expandable → click toggles inline-expand (the primary
              //     read-the-row gesture).
              //   • not expandable, onRowClick set → click navigates.
              // Cells that need to navigate inside an expandable row (e.g.
              // username → user drilldown) wrap their own <Link> with
              // stopPropagation so the link wins over the toggle.
              const rowOnClick = expandable
                ? () => onToggleExpanded?.(row)
                : onRowClick
                  ? () => onRowClick(row)
                  : undefined;
              const rowInteractive = expandable || !!onRowClick;
              return (
                <React.Fragment key={key}>
                  <TableRow
                    data-testid="sortable-row"
                    data-row-key={key}
                    data-expanded={isExpanded ? 'true' : 'false'}
                    onClick={rowOnClick}
                    role={rowInteractive ? 'button' : undefined}
                    tabIndex={rowInteractive ? 0 : undefined}
                    aria-expanded={expandable ? isExpanded : undefined}
                    onKeyDown={(e) => {
                      if (expandable && onToggleExpanded) {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onToggleExpanded(row);
                          return;
                        }
                        if (e.key === 'Escape' && isExpanded) {
                          e.preventDefault();
                          onToggleExpanded(row);
                          return;
                        }
                      }
                      if (
                        !expandable &&
                        onRowClick &&
                        (e.key === 'Enter' || e.key === ' ')
                      ) {
                        e.preventDefault();
                        onRowClick(row);
                      }
                    }}
                    className={cn(
                      'min-h-11',
                      rowInteractive &&
                        'cursor-pointer hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                    )}
                  >
                    {columns.map((c, colIdx) => {
                      const raw = row[c.key];
                      const isFirstSticky = stickyFirstColumn && colIdx === 0;
                      return (
                        <TableCell
                          key={c.key}
                          className={cn(
                            'px-3',
                            c.align === 'right' && 'text-right tabular-nums',
                            c.width,
                            isFirstSticky && STICKY_FIRST_COL_CLASS,
                          )}
                        >
                          {c.format ? c.format(raw, row) : String(raw ?? '')}
                        </TableCell>
                      );
                    })}
                    {expandable && (
                      <TableCell className="px-2 w-10 text-right">
                        <button
                          type="button"
                          data-testid="sortable-row-expand"
                          aria-expanded={isExpanded}
                          aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (onToggleExpanded) onToggleExpanded(row);
                          }}
                          className="inline-flex items-center justify-center h-7 w-7 rounded hover:bg-muted text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                        >
                          <span aria-hidden="true">
                            {isExpanded ? '▾' : '▸'}
                          </span>
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                  {isExpanded && (
                    <TableRow
                      data-testid="sortable-row-expanded"
                      data-row-key={key}
                      // The expanded sub-row should not pick up the parent
                      // row's hover / focus styling — strip onRowClick
                      // semantics by NOT setting onClick / role here.
                    >
                      <TableCell
                        colSpan={totalCols}
                        className="bg-muted/20 p-4"
                      >
                        {renderExpanded!(row)}
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        )}
      </Table>

      {showLoadMore && (
        <div className="flex justify-center border-t border-border p-2">
          <button
            type="button"
            onClick={onLoadMore}
            data-testid="sortable-load-more"
            className="text-sm font-medium text-primary hover:underline"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  );

  // When no mobile renderer is provided, return the desktop table as-is
  // (legacy call sites continue to get overflow-x-auto + sticky first col
  // at every viewport). When a renderer IS provided, render both so the
  // breakpoint is a pure CSS swap.
  if (!mobileRenderFn) return desktopTable;
  return (
    <>
      {mobileCards}
      {desktopTable}
    </>
  );
}
